import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Step,
  StepLabel,
  Stepper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Preview as PreviewIcon,
  PlayArrow as PlayArrowIcon,
  RestartAlt as RestartAltIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import Papa from 'papaparse';
import { adminFetch } from '../utils/adminFetch';
import { getAdminCourseName } from '../utils/courseDisplay';
import {
  DISABILITY_FALSE_VALUE,
  DISABILITY_TRUE_VALUE,
  DISABILITY_UNSET_VALUE,
  formatCsvMappingLabel,
  LANGUAGE_UNSET_VALUE,
  SUPPORTED_USER_LANGUAGES,
} from '../utils/supportedLanguages';

const NONE_COLUMN = '__none__';
const EMPTY_DISTINCT_VALUES: string[] = [];

type CsvRow = Record<string, string>;

type ParsedCsv = {
  columnKeys: string[];
  columnLabels: string[];
  dataRows: Array<{ lineNumber: number; row: CsvRow }>;
  previewLines: Array<{ lineNumber: number; cells: string[]; isHeader: boolean }>;
};

type CompanyOption = { id: string; name: string };
type CourseOption = {
  id: string;
  courseProject?: string;
  title: string | Record<string, string>;
  defaultLanguage?: string;
};
type GroupOption = { id: string; name: string };

type ImportRowStatus =
  | 'created_and_enrolled'
  | 'enrolled_existing_user'
  | 'skipped_incomplete'
  | 'skipped_already_enrolled'
  | 'skipped_duplicate_in_file'
  | 'error';

type ImportRowResult = {
  rowNumber: number;
  email: string;
  firstName: string;
  lastName: string;
  status: ImportRowStatus;
  message: string;
};

type ImportSummary = {
  totalRows: number;
  created: number;
  enrolledExisting: number;
  skippedIncomplete: number;
  skippedAlreadyEnrolled: number;
  skippedDuplicateInFile: number;
  errors: number;
  rows: ImportRowResult[];
};

const STEPS = ['Fichier CSV', 'Mapping', 'Configuration', 'Prévisualisation', 'Rapport'];

const PARAM_FIELD_BOX_SX = {
  px: 2,
  py: 1.5,
  borderRadius: 1.5,
  border: '1px solid',
  borderColor: 'divider',
  height: '100%',
  minHeight: 88,
  display: 'flex',
  alignItems: 'center',
  boxSizing: 'border-box',
} as const;

const MAPPING_SECTION_SX = {
  p: 2.5,
  mb: 3,
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 2,
  bgcolor: 'background.paper',
} as const;

const RAW_TABLE_THEME = {
  headerBg: '#281e30',
  zebraEven: 'rgba(40, 30, 48, 0.05)',
  zebraOdd: 'rgba(40, 30, 48, 0.11)',
  headerRowBg: 'rgba(73, 255, 193, 0.18)',
} as const;

const MAPPED_TABLE_THEME = {
  headerBg: '#2eab80',
  zebraEven: 'rgba(46, 171, 128, 0.07)',
  zebraOdd: 'rgba(46, 171, 128, 0.14)',
} as const;

const tableHeaderCellSx = (bg: string) => ({
  bgcolor: bg,
  color: '#fff',
  fontWeight: 700,
  fontSize: '0.8rem',
  letterSpacing: '0.02em',
  textTransform: 'uppercase' as const,
  whiteSpace: 'nowrap' as const,
  borderBottom: 'none',
});

const zebraRowSx = (index: number, evenBg: string, oddBg: string) => ({
  bgcolor: index % 2 === 0 ? evenBg : oddBg,
});

const MappingSectionTitle: React.FC<{ title: string; description?: string }> = ({
  title,
  description,
}) => (
  <Box mb={2}>
    <Typography variant="subtitle1" fontWeight={700}>
      {title}
    </Typography>
    {description && (
      <Typography variant="body2" color="text.secondary" mt={0.5}>
        {description}
      </Typography>
    )}
  </Box>
);

type MappedImportRow = {
  rowNumber: number;
  firstName: string;
  lastName: string;
  email: string;
  team?: string;
  position?: string;
  groupValue?: string;
  languageValue?: string;
  disabilityValue?: string;
};

const collectDistinctCsvValues = (values: Array<string | undefined>): string[] => {
  const distinct = Array.from(new Set(values.map((value) => (value ?? '').trim())));
  return distinct.sort((a, b) => {
    if (a === '' && b !== '') return -1;
    if (b === '' && a !== '') return 1;
    return a.localeCompare(b, 'fr');
  });
};

const STATUS_LABELS: Record<ImportRowStatus, string> = {
  created_and_enrolled: 'Créé et inscrit',
  enrolled_existing_user: 'Utilisateur existant',
  skipped_incomplete: 'Ignoré (incomplet)',
  skipped_already_enrolled: 'Ignoré (déjà inscrit)',
  skipped_duplicate_in_file: 'Ignoré (doublon fichier)',
  error: 'Erreur',
};

const STATUS_COLORS: Record<ImportRowStatus, 'success' | 'warning' | 'default' | 'error'> = {
  created_and_enrolled: 'success',
  enrolled_existing_user: 'warning',
  skipped_incomplete: 'default',
  skipped_already_enrolled: 'default',
  skipped_duplicate_in_file: 'default',
  error: 'error',
};

const guessColumn = (
  columnLabels: string[],
  columnKeys: string[],
  patterns: RegExp[]
): string => {
  const index = columnLabels.findIndex((label) =>
    patterns.some((p) => p.test(label.trim().toLowerCase()))
  );
  return index >= 0 ? columnKeys[index] : '';
};

const isRowEmpty = (cells: string[]): boolean =>
  cells.every((cell) => !String(cell || '').trim());

const makeUniqueHeaderKeys = (cells: string[]): { keys: string[]; labels: string[] } => {
  const keys: string[] = [];
  const labels: string[] = [];
  const counts = new Map<string, number>();

  cells.forEach((cell, index) => {
    const label = cell.trim() || `Colonne ${index + 1}`;
    const count = counts.get(label) || 0;
    counts.set(label, count + 1);
    const key = count === 0 ? `col_${index}` : `col_${index}_${count}`;
    keys.push(key);
    labels.push(count === 0 ? label : `${label} (${count + 1})`);
  });

  return { keys, labels };
};

const parseCsvLines = (
  csvRawLines: string[][],
  skipLines: number,
  hasHeaderRow: boolean
): ParsedCsv | null => {
  const linesAfterSkip = csvRawLines.slice(skipLines).filter((line) => Array.isArray(line));

  if (linesAfterSkip.length === 0) {
    return null;
  }

  const columnCount = Math.max(...linesAfterSkip.map((line) => line.length), 0);
  if (columnCount === 0) {
    return null;
  }

  const padRow = (cells: string[]): string[] => {
    const padded = [...cells];
    while (padded.length < columnCount) {
      padded.push('');
    }
    return padded.slice(0, columnCount);
  };

  let columnKeys: string[];
  let columnLabels: string[];
  let dataMatrix: string[][];
  let dataStartLine: number;

  const firstLineFileNumber = skipLines + 1;

  if (hasHeaderRow) {
    if (linesAfterSkip.length < 2) {
      return null;
    }
    const headerCells = padRow(linesAfterSkip[0]);
    const headerInfo = makeUniqueHeaderKeys(headerCells);
    columnKeys = headerInfo.keys;
    columnLabels = headerInfo.labels;
    dataMatrix = linesAfterSkip.slice(1).map(padRow);
    dataStartLine = firstLineFileNumber + 1;
  } else {
    columnKeys = Array.from({ length: columnCount }, (_, i) => `col_${i}`);
    columnLabels = columnKeys.map((_, i) => `Colonne ${i + 1}`);
    dataMatrix = linesAfterSkip.map(padRow);
    dataStartLine = firstLineFileNumber;
  }

  const dataRows = dataMatrix
    .map((row, index) => ({
      lineNumber: dataStartLine + index,
      row: (() => {
        const record: CsvRow = {};
        columnKeys.forEach((key, colIndex) => {
          record[key] = row[colIndex] || '';
        });
        return record;
      })(),
    }))
    .filter(({ row }) => !isRowEmpty(Object.values(row)));

  const previewSource = linesAfterSkip.slice(0, 4);
  const previewLines = previewSource.map((line, index) => ({
    lineNumber: skipLines + index + 1,
    cells: padRow(line),
    isHeader: hasHeaderRow && index === 0,
  }));

  return {
    columnKeys,
    columnLabels,
    dataRows,
    previewLines,
  };
};

const StudentImport: React.FC = () => {
  const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

  const [activeStep, setActiveStep] = useState(0);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const [csvRawLines, setCsvRawLines] = useState<string[][]>([]);
  const [skipLines, setSkipLines] = useState(0);
  const [hasHeaderRow, setHasHeaderRow] = useState(true);
  const [fileName, setFileName] = useState('');

  const [firstNameCol, setFirstNameCol] = useState('');
  const [lastNameCol, setLastNameCol] = useState('');
  const [emailCol, setEmailCol] = useState('');
  const [teamCol, setTeamCol] = useState(NONE_COLUMN);
  const [positionCol, setPositionCol] = useState(NONE_COLUMN);
  const [groupCol, setGroupCol] = useState(NONE_COLUMN);
  const [languageCol, setLanguageCol] = useState(NONE_COLUMN);
  const [disabilityCol, setDisabilityCol] = useState(NONE_COLUMN);

  const parsedCsv = useMemo(
    () => parseCsvLines(csvRawLines, skipLines, hasHeaderRow),
    [csvRawLines, skipLines, hasHeaderRow]
  );

  const columnKeys = parsedCsv?.columnKeys ?? [];
  const columnLabels = parsedCsv?.columnLabels ?? [];
  const dataRows = parsedCsv?.dataRows ?? [];
  const previewLines = parsedCsv?.previewLines ?? [];

  const [companies, setCompanies] = useState<CompanyOption[]>([]);
  const [courses, setCourses] = useState<CourseOption[]>([]);
  const [groups, setGroups] = useState<GroupOption[]>([]);
  const [companyId, setCompanyId] = useState('');
  const [courseId, setCourseId] = useState('');
  const [fixedGroupId, setFixedGroupId] = useState('');
  const [fixedDefaultLanguage, setFixedDefaultLanguage] = useState(LANGUAGE_UNSET_VALUE);
  const [groupMappings, setGroupMappings] = useState<Record<string, string>>({});
  const [languageMappings, setLanguageMappings] = useState<Record<string, string>>({});
  const [disabilityMappings, setDisabilityMappings] = useState<Record<string, string>>({});

  const [previewSummary, setPreviewSummary] = useState<ImportSummary | null>(null);
  const [finalSummary, setFinalSummary] = useState<ImportSummary | null>(null);

  const mappedRows = useMemo<MappedImportRow[]>(
    () =>
      dataRows.map(({ lineNumber, row }) => {
        const mapped: MappedImportRow = {
          rowNumber: lineNumber,
          firstName: (row[firstNameCol] || '').trim(),
          lastName: (row[lastNameCol] || '').trim(),
          email: (row[emailCol] || '').trim(),
        };

        if (teamCol !== NONE_COLUMN) mapped.team = (row[teamCol] || '').trim();
        if (positionCol !== NONE_COLUMN) mapped.position = (row[positionCol] || '').trim();
        if (groupCol !== NONE_COLUMN) mapped.groupValue = (row[groupCol] || '').trim();
        if (languageCol !== NONE_COLUMN) mapped.languageValue = (row[languageCol] || '').trim();
        if (disabilityCol !== NONE_COLUMN) mapped.disabilityValue = (row[disabilityCol] || '').trim();

        return mapped;
      }),
    [
      dataRows,
      firstNameCol,
      lastNameCol,
      emailCol,
      teamCol,
      positionCol,
      groupCol,
      languageCol,
      disabilityCol,
    ]
  );

  const distinctGroupValues = useMemo(
    () =>
      groupCol !== NONE_COLUMN
        ? collectDistinctCsvValues(mappedRows.map((row) => row.groupValue))
        : EMPTY_DISTINCT_VALUES,
    [groupCol, mappedRows]
  );

  const distinctLanguageValues = useMemo(
    () =>
      languageCol !== NONE_COLUMN
        ? collectDistinctCsvValues(mappedRows.map((row) => row.languageValue))
        : EMPTY_DISTINCT_VALUES,
    [languageCol, mappedRows]
  );

  const distinctDisabilityValues = useMemo(
    () =>
      disabilityCol !== NONE_COLUMN
        ? collectDistinctCsvValues(mappedRows.map((row) => row.disabilityValue))
        : EMPTY_DISTINCT_VALUES,
    [disabilityCol, mappedRows]
  );

  const distinctGroupValuesKey = distinctGroupValues.join('\0');
  const distinctLanguageValuesKey = distinctLanguageValues.join('\0');
  const distinctDisabilityValuesKey = distinctDisabilityValues.join('\0');

  const previewSample = useMemo(() => mappedRows.slice(0, 4), [mappedRows]);

  const columnStructureKey = columnKeys.join('\0');

  useEffect(() => {
    if (!columnStructureKey) return;

    const validKeys = new Set(columnKeys);
    const keepOrGuess = (current: string, patterns: RegExp[]) => {
      if (current && validKeys.has(current)) return current;
      const guessed = guessColumn(columnLabels, columnKeys, patterns);
      return guessed || columnKeys[0] || '';
    };

    setFirstNameCol((current) =>
      keepOrGuess(current, [/pr[eé]nom/, /first.?name/, /^prenom$/, /^firstname$/])
    );
    setLastNameCol((current) =>
      keepOrGuess(current, [/^nom$/, /last.?name/, /^name$/])
    );
    setEmailCol((current) =>
      keepOrGuess(current, [/e-?mail/, /courriel/, /mail/])
    );
  }, [columnStructureKey, columnKeys, columnLabels]);

  const loadTargets = useCallback(async () => {
    const [companiesRes, coursesRes] = await Promise.all([
      adminFetch(`${apiBase}/companies`),
      adminFetch(`${apiBase}/courses`),
    ]);

    if (!companiesRes.ok) {
      throw new Error('Impossible de charger les entreprises');
    }
    if (!coursesRes.ok) {
      throw new Error('Impossible de charger les formations');
    }

    const companiesData = await companiesRes.json();
    const coursesData = await coursesRes.json();

    setCompanies(
      (companiesData?.companies || []).map((c: { id: string; name: string }) => ({
        id: c.id,
        name: c.name,
      }))
    );
    setCourses(
      (coursesData || []).map((c: CourseOption & { id?: string; _id?: string }) => ({
        id: c.id || (c as any)._id,
        courseProject: c.courseProject,
        title: c.title,
        defaultLanguage: c.defaultLanguage,
      }))
    );
  }, [apiBase]);

  useEffect(() => {
    void loadTargets().catch((err: Error) => setError(err.message));
  }, [loadTargets]);

  useEffect(() => {
    if (!courseId) {
      setGroups([]);
      setFixedGroupId('');
      return;
    }

    void (async () => {
      const res = await adminFetch(`${apiBase}/groups/course/${courseId}`);
      if (!res.ok) {
        setError('Impossible de charger les groupes');
        return;
      }
      const data = await res.json();
      const normalized = (data || []).map((g: { id?: string; _id?: string; name: string }) => ({
        id: g.id || g._id || '',
        name: g.name,
      }));
      setGroups(normalized);
      setFixedGroupId(normalized[0]?.id || '');
    })();
  }, [apiBase, courseId]);

  useEffect(() => {
    if (groupCol === NONE_COLUMN) {
      setGroupMappings((prev) => (Object.keys(prev).length === 0 ? prev : {}));
      return;
    }
    setGroupMappings((prev) => {
      const next: Record<string, string> = {};
      distinctGroupValues.forEach((value) => {
        next[value] = prev[value] || '';
      });
      const prevKeys = Object.keys(prev);
      const nextKeys = Object.keys(next);
      if (
        prevKeys.length === nextKeys.length &&
        nextKeys.every((key) => prev[key] === next[key])
      ) {
        return prev;
      }
      return next;
    });
  }, [groupCol, distinctGroupValuesKey, distinctGroupValues]);

  useEffect(() => {
    if (languageCol === NONE_COLUMN) {
      setLanguageMappings((prev) => (Object.keys(prev).length === 0 ? prev : {}));
      return;
    }
    setLanguageMappings((prev) => {
      const next: Record<string, string> = {};
      distinctLanguageValues.forEach((value) => {
        next[value] = prev[value] || '';
      });
      const prevKeys = Object.keys(prev);
      const nextKeys = Object.keys(next);
      if (
        prevKeys.length === nextKeys.length &&
        nextKeys.every((key) => prev[key] === next[key])
      ) {
        return prev;
      }
      return next;
    });
  }, [languageCol, distinctLanguageValuesKey, distinctLanguageValues]);

  useEffect(() => {
    if (disabilityCol === NONE_COLUMN) {
      setDisabilityMappings((prev) => (Object.keys(prev).length === 0 ? prev : {}));
      return;
    }
    setDisabilityMappings((prev) => {
      const next: Record<string, string> = {};
      distinctDisabilityValues.forEach((value) => {
        next[value] = prev[value] || '';
      });
      const prevKeys = Object.keys(prev);
      const nextKeys = Object.keys(next);
      if (
        prevKeys.length === nextKeys.length &&
        nextKeys.every((key) => prev[key] === next[key])
      ) {
        return prev;
      }
      return next;
    });
  }, [disabilityCol, distinctDisabilityValuesKey, distinctDisabilityValues]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setError('');
    setPreviewSummary(null);
    setFinalSummary(null);

    Papa.parse(file, {
      header: false,
      skipEmptyLines: false,
      complete: (results) => {
        const lines = (results.data as string[][]).filter((line) => Array.isArray(line));

        if (lines.length === 0) {
          setError('Le fichier CSV est vide ou invalide');
          return;
        }

        setCsvRawLines(lines);
        setSkipLines(0);
        setHasHeaderRow(true);
        setFileName(file.name);
        setFirstNameCol('');
        setLastNameCol('');
        setEmailCol('');
        setTeamCol(NONE_COLUMN);
        setPositionCol(NONE_COLUMN);
        setGroupCol(NONE_COLUMN);
        setLanguageCol(NONE_COLUMN);
        setDisabilityCol(NONE_COLUMN);
        setActiveStep(1);
      },
      error: () => setError('Erreur lors de la lecture du fichier CSV'),
    });

    event.target.value = '';
  };

  const canProceedFromMapping =
    Boolean(firstNameCol && lastNameCol && emailCol) && mappedRows.length > 0;

  const isMappingComplete = (values: string[], mappings: Record<string, string>) =>
    values.every((value) => Boolean(mappings[value]));

  const canProceedFromConfiguration = useMemo(() => {
    if (!companyId || !courseId) return false;
    if (groupCol !== NONE_COLUMN) {
      if (!isMappingComplete(distinctGroupValues, groupMappings)) return false;
    } else if (!fixedGroupId) {
      return false;
    }
    if (languageCol !== NONE_COLUMN && !isMappingComplete(distinctLanguageValues, languageMappings)) {
      return false;
    }
    if (
      disabilityCol !== NONE_COLUMN &&
      !isMappingComplete(distinctDisabilityValues, disabilityMappings)
    ) {
      return false;
    }
    return true;
  }, [
    companyId,
    courseId,
    groupCol,
    fixedGroupId,
    distinctGroupValues,
    groupMappings,
    languageCol,
    distinctLanguageValues,
    languageMappings,
    disabilityCol,
    distinctDisabilityValues,
    disabilityMappings,
  ]);

  const buildImportPayload = () => ({
    companyId,
    courseId,
    groupMode: groupCol !== NONE_COLUMN ? 'column' : 'fixed',
    fixedGroupId: groupCol !== NONE_COLUMN ? undefined : fixedGroupId,
    groupMappings: groupCol !== NONE_COLUMN ? groupMappings : undefined,
    languageMode: languageCol !== NONE_COLUMN ? 'column' : 'fixed',
    fixedDefaultLanguage:
      languageCol === NONE_COLUMN && fixedDefaultLanguage !== LANGUAGE_UNSET_VALUE
        ? fixedDefaultLanguage
        : undefined,
    languageMappings:
      languageCol !== NONE_COLUMN
      ? Object.fromEntries(
          Object.entries(languageMappings).map(([key, value]) => [
            key,
            value === LANGUAGE_UNSET_VALUE ? null : value,
          ])
        )
      : undefined,
    disabilityMode: disabilityCol !== NONE_COLUMN ? 'column' : 'none',
    disabilityMappings:
      disabilityCol !== NONE_COLUMN
      ? Object.fromEntries(
          Object.entries(disabilityMappings).map(([key, value]) => [
            key,
            value === DISABILITY_UNSET_VALUE ? null : value === DISABILITY_TRUE_VALUE,
          ])
        )
      : undefined,
    rows: mappedRows,
  });

  const callImport = async (dryRun: boolean): Promise<ImportSummary> => {
    const endpoint = dryRun ? 'preview' : 'execute';
    const res = await adminFetch(`${apiBase}/students/import/${endpoint}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(buildImportPayload()),
    });

    const data = await res.json();
    if (!res.ok) {
      throw new Error(data?.error || data?.message || 'Erreur lors de l\'import');
    }
    return data.summary as ImportSummary;
  };

  const handlePreview = async () => {
    setLoading(true);
    setError('');
    try {
      const summary = await callImport(true);
      setPreviewSummary(summary);
      setActiveStep(3);
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la prévisualisation');
    } finally {
      setLoading(false);
    }
  };

  const handleExecute = async () => {
    setLoading(true);
    setError('');
    try {
      const summary = await callImport(false);
      setFinalSummary(summary);
      setActiveStep(4);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err: any) {
      setError(err.message || 'Erreur lors de l\'import');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setActiveStep(0);
    setError('');
    setCsvRawLines([]);
    setSkipLines(0);
    setHasHeaderRow(true);
    setFileName('');
    setFirstNameCol('');
    setLastNameCol('');
    setEmailCol('');
    setTeamCol(NONE_COLUMN);
    setPositionCol(NONE_COLUMN);
    setGroupCol(NONE_COLUMN);
    setLanguageCol(NONE_COLUMN);
    setDisabilityCol(NONE_COLUMN);
    setCompanyId('');
    setCourseId('');
    setFixedGroupId('');
    setFixedDefaultLanguage(LANGUAGE_UNSET_VALUE);
    setGroupMappings({});
    setLanguageMappings({});
    setDisabilityMappings({});
    setPreviewSummary(null);
    setFinalSummary(null);
  };

  const handleSkipLinesChange = (value: string) => {
    const parsed = Number.parseInt(value, 10);
    setSkipLines(Number.isFinite(parsed) && parsed >= 0 ? parsed : 0);
  };

  const renderColumnOptions = (includeNone = false) =>
    [
      includeNone ? (
        <MenuItem key="__none__" value={NONE_COLUMN}>
          Aucune
        </MenuItem>
      ) : null,
      ...columnKeys.map((key, index) => (
        <MenuItem key={key} value={key}>
          {columnLabels[index]}
        </MenuItem>
      )),
    ];

  const renderImportSuccessMessage = (summary: ImportSummary) => {
    const parts: string[] = [];
    if (summary.created > 0) {
      parts.push(`${summary.created} compte${summary.created > 1 ? 's' : ''} créé${summary.created > 1 ? 's' : ''}`);
    }
    if (summary.enrolledExisting > 0) {
      parts.push(
        `${summary.enrolledExisting} utilisateur${summary.enrolledExisting > 1 ? 's' : ''} existant${summary.enrolledExisting > 1 ? 's' : ''} inscrit${summary.enrolledExisting > 1 ? 's' : ''}`
      );
    }
    const skipped =
      summary.skippedIncomplete +
      summary.skippedAlreadyEnrolled +
      summary.skippedDuplicateInFile;
    if (skipped > 0) {
      parts.push(`${skipped} ligne${skipped > 1 ? 's' : ''} ignorée${skipped > 1 ? 's' : ''}`);
    }
    if (summary.errors > 0) {
      parts.push(`${summary.errors} erreur${summary.errors > 1 ? 's' : ''}`);
    }

    const detail = parts.length > 0 ? parts.join(', ') : 'Aucune modification effectuée';
    return `L'import a bien été exécuté. ${detail}. Consultez le rapport détaillé ci-dessous.`;
  };

  const renderSummaryCards = (summary: ImportSummary) => (
    <Box display="flex" flexWrap="wrap" gap={1} mb={3}>
      <Chip label={`Total : ${summary.totalRows}`} />
      <Chip color="success" label={`Créés : ${summary.created}`} />
      <Chip color="warning" label={`Existants inscrits : ${summary.enrolledExisting}`} />
      <Chip label={`Ignorés (incomplets) : ${summary.skippedIncomplete}`} />
      <Chip label={`Ignorés (déjà inscrits) : ${summary.skippedAlreadyEnrolled}`} />
      <Chip label={`Ignorés (doublons) : ${summary.skippedDuplicateInFile}`} />
      {summary.errors > 0 && <Chip color="error" label={`Erreurs : ${summary.errors}`} />}
    </Box>
  );

  const renderReportTable = (summary: ImportSummary) => (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Ligne</TableCell>
            <TableCell>Prénom</TableCell>
            <TableCell>Nom</TableCell>
            <TableCell>Email</TableCell>
            <TableCell>Statut</TableCell>
            <TableCell>Détail</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {summary.rows.map((row) => (
            <TableRow key={`${row.rowNumber}-${row.email}`}>
              <TableCell>{row.rowNumber}</TableCell>
              <TableCell>{row.firstName}</TableCell>
              <TableCell>{row.lastName}</TableCell>
              <TableCell>{row.email}</TableCell>
              <TableCell>
                <Chip
                  size="small"
                  color={STATUS_COLORS[row.status]}
                  label={STATUS_LABELS[row.status]}
                />
              </TableCell>
              <TableCell>{row.message}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const selectedCompany = companies.find((c) => c.id === companyId);
  const selectedCourse = courses.find((c) => c.id === courseId);
  const selectedGroup = groups.find((g) => g.id === fixedGroupId);

  const renderValueMappingTable = (
    title: string,
    description: string,
    values: string[],
    renderTargetSelect: (value: string) => React.ReactNode
  ) => (
    <Paper variant="outlined" sx={{ ...MAPPING_SECTION_SX, mb: 2 }}>
      <MappingSectionTitle title={title} description={description} />
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={tableHeaderCellSx(RAW_TABLE_THEME.headerBg)}>Valeur CSV</TableCell>
              <TableCell sx={tableHeaderCellSx(RAW_TABLE_THEME.headerBg)}>Correspondance</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {values.map((value, index) => (
              <TableRow
                key={`map-${title}-${value}`}
                sx={zebraRowSx(index, RAW_TABLE_THEME.zebraEven, RAW_TABLE_THEME.zebraOdd)}
              >
                <TableCell sx={{ fontWeight: 600 }}>{formatCsvMappingLabel(value)}</TableCell>
                <TableCell>{renderTargetSelect(value)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );

  return (
    <Container maxWidth="lg">
      <Box mb={3}>
        <Typography variant="h4" gutterBottom>
          Import d&apos;apprenants
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Importez des apprenants depuis un fichier CSV, mappez les colonnes, puis prévisualisez avant de confirmer.
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {STEPS.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {activeStep === 0 && (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <CloudUploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Sélectionnez un fichier CSV
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>
              Colonnes attendues : prénom, nom, email (l&apos;ordre et les intitulés peuvent varier).
            </Typography>
            <Button variant="contained" component="label">
              Choisir un fichier
              <input type="file" accept=".csv,text/csv" hidden onChange={handleFileChange} />
            </Button>
          </CardContent>
        </Card>
      )}

      {activeStep === 1 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Fichier : {fileName}
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>
              {dataRows.length} ligne{dataRows.length > 1 ? 's' : ''} de données détectée
              {dataRows.length !== 1 ? 's' : ''}
            </Typography>

            {!parsedCsv && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Aucune ligne de données exploitable avec ces paramètres. Vérifiez le nombre de lignes
                ignorées et l&apos;option en-têtes.
              </Alert>
            )}

            <Paper variant="outlined" sx={MAPPING_SECTION_SX}>
              <MappingSectionTitle
                title="Paramètres du fichier"
                description="Indiquez combien de lignes ignorer en tête de fichier et si la première ligne utile contient les en-têtes de colonnes."
              />
              <Box
                display="grid"
                gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr' }}
                gap={2}
                alignItems="stretch"
              >
                <Paper variant="outlined" sx={PARAM_FIELD_BOX_SX}>
                  <TextField
                    label="Lignes à ignorer"
                    type="number"
                    value={skipLines}
                    onChange={(e) => handleSkipLinesChange(e.target.value)}
                    inputProps={{ min: 0 }}
                    fullWidth
                  />
                </Paper>
                <Paper
                  variant="outlined"
                  sx={{
                    ...PARAM_FIELD_BOX_SX,
                    bgcolor: hasHeaderRow ? 'rgba(46, 171, 128, 0.08)' : 'action.hover',
                    borderColor: hasHeaderRow ? 'primary.light' : 'divider',
                  }}
                >
                  <FormControlLabel
                    sx={{ m: 0, width: '100%', alignItems: 'center' }}
                    control={
                      <Switch
                        checked={hasHeaderRow}
                        onChange={(e) => setHasHeaderRow(e.target.checked)}
                        color="primary"
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2" fontWeight={600}>
                          Utiliser la première ligne comme en-têtes
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {hasHeaderRow
                            ? 'Les noms de colonnes du CSV seront proposés dans le mapping.'
                            : 'Les colonnes seront identifiées par leur numéro (Colonne 1, 2, …).'}
                        </Typography>
                      </Box>
                    }
                  />
                </Paper>
              </Box>
            </Paper>

            {previewLines.length > 0 && (
              <Paper variant="outlined" sx={MAPPING_SECTION_SX}>
                <MappingSectionTitle
                  title="Aperçu brut"
                  description="Les 4 premières lignes non ignorées du fichier, pour vérifier le découpage."
                />
                <TableContainer sx={{ maxWidth: '100%', overflowX: 'auto', borderRadius: 1 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={tableHeaderCellSx(RAW_TABLE_THEME.headerBg)}>Ligne</TableCell>
                        {columnLabels.map((label, index) => (
                          <TableCell key={`preview-head-${index}`} sx={tableHeaderCellSx(RAW_TABLE_THEME.headerBg)}>
                            {label}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {previewLines.map((line, index) => (
                        <TableRow
                          key={line.lineNumber}
                          sx={{
                            ...zebraRowSx(index, RAW_TABLE_THEME.zebraEven, RAW_TABLE_THEME.zebraOdd),
                            ...(line.isHeader ? { bgcolor: RAW_TABLE_THEME.headerRowBg } : {}),
                          }}
                        >
                          <TableCell sx={{ fontWeight: line.isHeader ? 700 : 500, whiteSpace: 'nowrap' }}>
                            {line.lineNumber}
                            {line.isHeader && (
                              <Chip
                                label="En-têtes"
                                size="small"
                                sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                                color="success"
                              />
                            )}
                          </TableCell>
                          {line.cells.map((cell, cellIndex) => (
                            <TableCell key={`${line.lineNumber}-${cellIndex}`}>{cell || '—'}</TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            )}

            <Paper variant="outlined" sx={MAPPING_SECTION_SX}>
              <MappingSectionTitle
                title="Correspondance des colonnes"
                description="Associez les champs obligatoires et, si besoin, les champs optionnels à une colonne du fichier."
              />
              <Typography variant="body2" fontWeight={600} color="text.secondary" mb={1.5}>
                Obligatoires
              </Typography>
              <Box display="grid" gridTemplateColumns={{ xs: '1fr', md: 'repeat(3, 1fr)' }} gap={2} mb={3}>
                <FormControl required fullWidth>
                  <InputLabel>Colonne prénom</InputLabel>
                  <Select
                    value={firstNameCol}
                    label="Colonne prénom"
                    onChange={(e) => setFirstNameCol(e.target.value)}
                  >
                    {renderColumnOptions()}
                  </Select>
                </FormControl>
                <FormControl required fullWidth>
                  <InputLabel>Colonne nom</InputLabel>
                  <Select
                    value={lastNameCol}
                    label="Colonne nom"
                    onChange={(e) => setLastNameCol(e.target.value)}
                  >
                    {renderColumnOptions()}
                  </Select>
                </FormControl>
                <FormControl required fullWidth>
                  <InputLabel>Colonne email</InputLabel>
                  <Select
                    value={emailCol}
                    label="Colonne email"
                    onChange={(e) => setEmailCol(e.target.value)}
                  >
                    {renderColumnOptions()}
                  </Select>
                </FormControl>
              </Box>
              <Typography variant="body2" fontWeight={600} color="text.secondary" mb={1.5}>
                Optionnels
              </Typography>
              <Box display="grid" gridTemplateColumns={{ xs: '1fr', md: 'repeat(3, 1fr)' }} gap={2}>
                <FormControl fullWidth>
                  <InputLabel>Colonne équipe</InputLabel>
                  <Select
                    value={teamCol}
                    label="Colonne équipe"
                    onChange={(e) => setTeamCol(e.target.value)}
                  >
                    {renderColumnOptions(true)}
                  </Select>
                </FormControl>
                <FormControl fullWidth>
                  <InputLabel>Colonne poste</InputLabel>
                  <Select
                    value={positionCol}
                    label="Colonne poste"
                    onChange={(e) => setPositionCol(e.target.value)}
                  >
                    {renderColumnOptions(true)}
                  </Select>
                </FormControl>
                <FormControl fullWidth>
                  <InputLabel>Colonne groupe</InputLabel>
                  <Select
                    value={groupCol}
                    label="Colonne groupe"
                    onChange={(e) => setGroupCol(e.target.value)}
                  >
                    {renderColumnOptions(true)}
                  </Select>
                </FormControl>
                <FormControl fullWidth>
                  <InputLabel>Colonne langue</InputLabel>
                  <Select
                    value={languageCol}
                    label="Colonne langue"
                    onChange={(e) => setLanguageCol(e.target.value)}
                  >
                    {renderColumnOptions(true)}
                  </Select>
                </FormControl>
                <FormControl fullWidth>
                  <InputLabel>Colonne handicap</InputLabel>
                  <Select
                    value={disabilityCol}
                    label="Colonne handicap"
                    onChange={(e) => setDisabilityCol(e.target.value)}
                  >
                    {renderColumnOptions(true)}
                  </Select>
                </FormControl>
              </Box>
            </Paper>

            <Paper variant="outlined" sx={{ ...MAPPING_SECTION_SX, mb: 0 }}>
              <MappingSectionTitle
                title="Aperçu mappé"
                description="Résultat du mapping sur les 4 premières lignes de données."
              />
              <TableContainer sx={{ borderRadius: 1 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={tableHeaderCellSx(MAPPED_TABLE_THEME.headerBg)}>Ligne</TableCell>
                      <TableCell sx={tableHeaderCellSx(MAPPED_TABLE_THEME.headerBg)}>Prénom</TableCell>
                      <TableCell sx={tableHeaderCellSx(MAPPED_TABLE_THEME.headerBg)}>Nom</TableCell>
                      <TableCell sx={tableHeaderCellSx(MAPPED_TABLE_THEME.headerBg)}>Email</TableCell>
                      {teamCol !== NONE_COLUMN && (
                        <TableCell sx={tableHeaderCellSx(MAPPED_TABLE_THEME.headerBg)}>Équipe</TableCell>
                      )}
                      {positionCol !== NONE_COLUMN && (
                        <TableCell sx={tableHeaderCellSx(MAPPED_TABLE_THEME.headerBg)}>Poste</TableCell>
                      )}
                      {groupCol !== NONE_COLUMN && (
                        <TableCell sx={tableHeaderCellSx(MAPPED_TABLE_THEME.headerBg)}>Groupe</TableCell>
                      )}
                      {languageCol !== NONE_COLUMN && (
                        <TableCell sx={tableHeaderCellSx(MAPPED_TABLE_THEME.headerBg)}>Langue</TableCell>
                      )}
                      {disabilityCol !== NONE_COLUMN && (
                        <TableCell sx={tableHeaderCellSx(MAPPED_TABLE_THEME.headerBg)}>Handicap</TableCell>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {previewSample.map((row, index) => (
                      <TableRow
                        key={row.rowNumber}
                        sx={zebraRowSx(index, MAPPED_TABLE_THEME.zebraEven, MAPPED_TABLE_THEME.zebraOdd)}
                      >
                        <TableCell sx={{ fontWeight: 500 }}>{row.rowNumber}</TableCell>
                        <TableCell>{row.firstName || '—'}</TableCell>
                        <TableCell>{row.lastName || '—'}</TableCell>
                        <TableCell>{row.email || '—'}</TableCell>
                        {teamCol !== NONE_COLUMN && <TableCell>{row.team || '—'}</TableCell>}
                        {positionCol !== NONE_COLUMN && <TableCell>{row.position || '—'}</TableCell>}
                        {groupCol !== NONE_COLUMN && (
                          <TableCell>{formatCsvMappingLabel(row.groupValue || '')}</TableCell>
                        )}
                        {languageCol !== NONE_COLUMN && (
                          <TableCell>{formatCsvMappingLabel(row.languageValue || '')}</TableCell>
                        )}
                        {disabilityCol !== NONE_COLUMN && (
                          <TableCell>{formatCsvMappingLabel(row.disabilityValue || '')}</TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>

            <Divider sx={{ my: 3 }} />

            <Box display="flex" justifyContent="space-between">
              <Button onClick={handleReset}>Changer de fichier</Button>
              <Button
                variant="contained"
                disabled={!canProceedFromMapping}
                onClick={() => setActiveStep(2)}
              >
                Continuer
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {activeStep === 2 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Configuration
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>
              Choisissez l&apos;entreprise et la formation, puis mappez les valeurs du fichier avec les
              données attendues par la plateforme.
            </Typography>

            <Paper variant="outlined" sx={MAPPING_SECTION_SX}>
              <MappingSectionTitle title="Cible de l'import" />
              <Box display="grid" gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }} gap={2}>
                <FormControl required fullWidth>
                  <InputLabel>Entreprise</InputLabel>
                  <Select
                    value={companyId}
                    label="Entreprise"
                    onChange={(e) => setCompanyId(e.target.value)}
                  >
                    {companies.map((c) => (
                      <MenuItem key={c.id} value={c.id}>
                        {c.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl required fullWidth disabled={!companyId}>
                  <InputLabel>Formation</InputLabel>
                  <Select
                    value={courseId}
                    label="Formation"
                    onChange={(e) => setCourseId(e.target.value)}
                  >
                    {courses.map((c) => (
                      <MenuItem key={c.id} value={c.id}>
                        {getAdminCourseName(c)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Paper>

            {courseId && groupCol === NONE_COLUMN && (
              <Paper variant="outlined" sx={MAPPING_SECTION_SX}>
                <MappingSectionTitle
                  title="Groupe unique"
                  description="Aucune colonne groupe sélectionnée : tous les apprenants seront inscrits au même groupe."
                />
                <FormControl required fullWidth disabled={groups.length === 0}>
                  <InputLabel>Groupe</InputLabel>
                  <Select
                    value={fixedGroupId}
                    label="Groupe"
                    onChange={(e) => setFixedGroupId(e.target.value)}
                  >
                    {groups.map((g) => (
                      <MenuItem key={g.id} value={g.id}>
                        {g.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Paper>
            )}

            {courseId &&
              groupCol !== NONE_COLUMN &&
              renderValueMappingTable(
                'Mapping des groupes',
                'Associez chaque valeur de la colonne groupe du CSV à un groupe de la formation.',
                distinctGroupValues,
                (value) => (
                  <FormControl fullWidth size="small" required>
                    <InputLabel>Groupe</InputLabel>
                    <Select
                      value={groupMappings[value] || ''}
                      label="Groupe"
                      onChange={(e) =>
                        setGroupMappings((prev) => ({ ...prev, [value]: e.target.value }))
                      }
                    >
                      {groups.map((g) => (
                        <MenuItem key={g.id} value={g.id}>
                          {g.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )
              )}

            {languageCol === NONE_COLUMN && (
              <Paper variant="outlined" sx={MAPPING_SECTION_SX}>
                <MappingSectionTitle
                  title="Langue par défaut"
                  description="Aucune colonne langue sélectionnée : la même langue sera appliquée à tous les apprenants importés."
                />
                <FormControl fullWidth>
                  <InputLabel>Langue</InputLabel>
                  <Select
                    value={fixedDefaultLanguage}
                    label="Langue"
                    onChange={(e) => setFixedDefaultLanguage(e.target.value)}
                  >
                    <MenuItem value={LANGUAGE_UNSET_VALUE}>Non renseigné</MenuItem>
                    {SUPPORTED_USER_LANGUAGES.map((lang) => (
                      <MenuItem key={lang.code} value={lang.code}>
                        {lang.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Paper>
            )}

            {languageCol !== NONE_COLUMN &&
              renderValueMappingTable(
                'Mapping des langues',
                'Associez chaque valeur de la colonne langue à une langue supportée.',
                distinctLanguageValues,
                (value) => (
                  <FormControl fullWidth size="small" required>
                    <InputLabel>Langue</InputLabel>
                    <Select
                      value={languageMappings[value] || ''}
                      label="Langue"
                      onChange={(e) =>
                        setLanguageMappings((prev) => ({ ...prev, [value]: e.target.value }))
                      }
                    >
                      <MenuItem value={LANGUAGE_UNSET_VALUE}>Non renseigné</MenuItem>
                      {SUPPORTED_USER_LANGUAGES.map((lang) => (
                        <MenuItem key={lang.code} value={lang.code}>
                          {lang.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )
              )}

            {disabilityCol !== NONE_COLUMN &&
              renderValueMappingTable(
                'Mapping du handicap',
                'Associez chaque valeur de la colonne handicap à Oui, Non ou Non renseigné.',
                distinctDisabilityValues,
                (value) => (
                  <FormControl fullWidth size="small" required>
                    <InputLabel>Handicap</InputLabel>
                    <Select
                      value={disabilityMappings[value] || ''}
                      label="Handicap"
                      onChange={(e) =>
                        setDisabilityMappings((prev) => ({ ...prev, [value]: e.target.value }))
                      }
                    >
                      <MenuItem value={DISABILITY_TRUE_VALUE}>Oui</MenuItem>
                      <MenuItem value={DISABILITY_FALSE_VALUE}>Non</MenuItem>
                      <MenuItem value={DISABILITY_UNSET_VALUE}>Non renseigné</MenuItem>
                    </Select>
                  </FormControl>
                )
              )}

            <Box display="flex" justifyContent="space-between" mt={1}>
              <Button onClick={() => setActiveStep(1)}>Retour</Button>
              <Button
                variant="contained"
                startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <PreviewIcon />}
                disabled={!canProceedFromConfiguration || loading}
                onClick={() => void handlePreview()}
              >
                Prévisualiser l&apos;import
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {activeStep === 3 && previewSummary && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Prévisualisation
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              {mappedRows.length} lignes → {selectedCompany?.name} /{' '}
              {selectedCourse ? getAdminCourseName(selectedCourse) : ''}
              {groupCol === NONE_COLUMN && selectedGroup ? ` / ${selectedGroup.name}` : ''}
            </Typography>

            {renderSummaryCards(previewSummary)}
            {renderReportTable(previewSummary)}

            <Box display="flex" justifyContent="space-between" mt={3}>
              <Button onClick={() => setActiveStep(2)} disabled={loading}>
                Retour
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <PlayArrowIcon />}
                disabled={loading}
                onClick={() => void handleExecute()}
              >
                Confirmer l&apos;import
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {activeStep === 4 && finalSummary && (
        <Card>
          <CardContent>
            <Alert
              severity="success"
              icon={<CheckCircleIcon fontSize="inherit" />}
              sx={{ mb: 3 }}
            >
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                Import terminé
              </Typography>
              <Typography variant="body2">{renderImportSuccessMessage(finalSummary)}</Typography>
            </Alert>

            <Typography variant="h6" gutterBottom>
              Rapport d&apos;importation
            </Typography>
            {renderSummaryCards(finalSummary)}
            {renderReportTable(finalSummary)}
            <Box mt={3}>
              <Button variant="outlined" startIcon={<RestartAltIcon />} onClick={handleReset}>
                Nouvel import
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}
    </Container>
  );
};

export default StudentImport;
